import React, { useEffect, useRef, useState } from 'react';
import { apiFetchAuthed } from '../../apiHelpers';
import { formatLastSeen } from '../../utils/date-utils';
import type { LorePerson, LoreRow, LoreStatus } from './types';

const MAX_LINE = 200;

const WELL: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,.35)',
  border: 'none',
  borderRadius: 8,
  color: '#ddd',
  fontSize: 12,
  fontFamily: 'inherit',
  padding: '7px 10px',
};

const KEY_EDGE = 'inset 0 1px 0 rgba(255,255,255,.04), inset 0 -1px 0 rgba(0,0,0,.22)';

const byNewest = (a: LoreRow, b: LoreRow) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0);

type RowProps = {
  row: LoreRow;
  busy: boolean;
  glow: boolean;
  onMove: (row: LoreRow, status: Exclude<LoreStatus, 'pending'>) => void;
};

const LoreLine: React.FC<RowProps> = ({ row, busy, glow, onMove }) => {
  const canApprove = row.status !== 'approved';
  const canRetire = row.status !== 'retired';
  return (
    <li
      className="transition-shadow duration-700 motion-reduce:transition-none"
      style={{
        background: '#1a1a1a', borderRadius: 12, padding: '12px 14px',
        display: 'flex', alignItems: 'flex-start', gap: 12,
        boxShadow: glow ? '0 0 0 1px rgba(255,204,51,0.45), 0 0 12px rgba(255,204,51,0.15)' : '0 0 0 1px transparent',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: row.status === 'retired' ? '#888' : '#e6e6e6', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          "{row.line}"
        </div>
        <div style={{ marginTop: 6, display: 'flex', gap: 12, fontSize: 11, color: '#888', flexWrap: 'wrap' }}>
          {row.about_label && <span>about <span style={{ color: '#bbb' }}>{row.about_label}</span></span>}
          <span>{row.source === 'flerpie' ? 'overheard by Flerpie' : `written by ${row.author_label || 'an admin'}`}</span>
          <span>{formatLastSeen(row.created_at)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {canApprove && (
          <button
            type="button"
            onClick={() => onMove(row, 'approved')}
            disabled={busy}
            aria-label={`Approve: ${row.line}`}
            className="np-state outline-none focus-visible:ring-1 focus-visible:ring-primary/60 disabled:opacity-50"
            style={{
              padding: '5px 12px', fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap',
              background: 'rgba(255,204,51,0.15)', color: '#FFCC33',
              border: 'none', borderRadius: 6, cursor: busy ? 'default' : 'pointer', boxShadow: KEY_EDGE,
            }}
          >
            Approve
          </button>
        )}
        {canRetire && (
          <button
            type="button"
            onClick={() => onMove(row, 'retired')}
            disabled={busy}
            aria-label={`Retire: ${row.line}`}
            className="np-state outline-none focus-visible:ring-1 focus-visible:ring-primary/60 disabled:opacity-50"
            style={{
              padding: '5px 12px', fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap',
              background: '#262626', color: '#999',
              border: 'none', borderRadius: 6, cursor: busy ? 'default' : 'pointer', boxShadow: KEY_EDGE,
            }}
          >
            Retire
          </button>
        )}
      </div>
    </li>
  );
};

const Heading: React.FC<{ label: string; count: number; color?: string }> = ({ label, count, color = '#aaa' }) => (
  <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color }}>
    {label} <span style={{ fontWeight: 400, color: '#777', fontVariantNumeric: 'tabular-nums' }}>({count})</span>
  </h3>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ margin: 0, fontSize: 12, color: '#666' }}>{children}</p>
);

const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 };

const FlerpieRumorMillPanel: React.FC = () => {
  const [rows, setRows] = useState<LoreRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [people, setPeople] = useState<LorePerson[]>([]);
  const [draft, setDraft] = useState('');
  const [aboutId, setAboutId] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<string>>(() => new Set());
  const [glowId, setGlowId] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const glowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    apiFetchAuthed('/ai/lore')
      .then((data: { rows?: LoreRow[] } | null) => { if (alive) setRows((data && data.rows) || []); })
      .catch((err: Error) => { if (alive) setLoadError(err.message); });
    // The picker is optional on a line, so a failed people load leaves just "Nobody in particular".
    apiFetchAuthed('/ai/lore/people')
      .then((data: LorePerson[] | null) => { if (alive) setPeople(data || []); })
      .catch(() => {});
    return () => {
      alive = false;
      if (glowTimer.current) clearTimeout(glowTimer.current);
    };
  }, []);

  const glow = (id: string) => {
    if (glowTimer.current) clearTimeout(glowTimer.current);
    setGlowId(id);
    glowTimer.current = setTimeout(() => setGlowId(null), 1400);
  };

  const setBusyFor = (id: string, on: boolean) => setBusy((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const move = async (row: LoreRow, status: Exclude<LoreStatus, 'pending'>) => {
    if (busy.has(row.id)) return;
    const previous = row.status;
    setMoveError(null);
    setBusyFor(row.id, true);
    setRows((list) => (list || []).map((r) => (r.id === row.id ? { ...r, status } : r)));
    try {
      const updated: LoreRow | null = await apiFetchAuthed(`/ai/lore/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (updated && updated.id) setRows((list) => (list || []).map((r) => (r.id === row.id ? updated : r)));
      glow(row.id);
    } catch {
      setRows((list) => (list || []).map((r) => (r.id === row.id ? { ...r, status: previous } : r)));
      setMoveError(`That did not ${status === 'approved' ? 'approve' : 'retire'}. It is back where it was; try again in a moment.`);
    } finally {
      setBusyFor(row.id, false);
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const line = draft.trim();
    if (!line || saving) return;
    setSaving(true);
    setAddError(null);
    const person = people.find((p) => String(p.id) === aboutId);
    try {
      const created: LoreRow = await apiFetchAuthed('/ai/lore', {
        method: 'POST',
        body: JSON.stringify(person ? { line, about_user_id: person.id } : { line }),
      });
      setRows((list) => [created, ...(list || [])]);
      setDraft('');
      setAboutId('');
      glow(created.id);
    } catch {
      setAddError('That line did not save. Try again in a moment.');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div style={{ padding: 24, color: '#ff6b6b' }}>Could not load the rumor mill: {loadError}</div>;
  if (!rows) return <div style={{ padding: 24, color: '#666' }}>Loading the rumor mill...</div>;

  const sorted = [...rows].sort(byNewest);
  const pending = sorted.filter((r) => r.status === 'pending');
  const approved = sorted.filter((r) => r.status === 'approved');
  const retired = sorted.filter((r) => r.status === 'retired');

  const renderList = (list: LoreRow[]) => (
    <ul style={listStyle}>
      {list.map((r) => (
        <LoreLine key={r.id} row={r} busy={busy.has(r.id)} glow={glowId === r.id} onMove={move} />
      ))}
    </ul>
  );

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 960 }}>
      <form
        onSubmit={add}
        style={{ background: '#1a1a1a', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            readOnly={saving}
            maxLength={MAX_LINE}
            placeholder="A line worth keeping"
            aria-label="A line worth keeping"
            className="m-0 outline-none focus-visible:ring-1 focus-visible:ring-primary/50 placeholder:text-gray-600"
            style={{ ...WELL, flex: '1 1 320px', minWidth: 0 }}
          />
          <select
            value={aboutId}
            onChange={(e) => setAboutId(e.target.value)}
            disabled={saving}
            aria-label="About whom"
            className="m-0 outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
            style={{ ...WELL, flex: '0 1 200px', minWidth: 0, background: '#141414', cursor: 'pointer' }}
          >
            <option value="">Nobody in particular</option>
            {people.map((p) => (
              <option key={String(p.id)} value={String(p.id)}>{p.label}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={saving || !draft.trim()}
            className="np-state outline-none focus-visible:ring-1 focus-visible:ring-primary/60 disabled:opacity-50"
            style={{
              padding: '7px 14px', fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap',
              background: 'rgba(255,204,51,0.15)', color: '#FFCC33',
              border: 'none', borderRadius: 8, boxShadow: KEY_EDGE,
              cursor: saving || !draft.trim() ? 'default' : 'pointer',
            }}
          >
            {saving ? 'Adding...' : 'Add as approved'}
          </button>
        </div>
        {addError && <p className="text-red-300" style={{ margin: 0, fontSize: 11 }}>{addError}</p>}
      </form>

      {moveError && <p className="text-red-300" style={{ margin: 0, fontSize: 11 }}>{moveError}</p>}

      <section aria-label="Waiting for you" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Heading label="Waiting for you" count={pending.length} color="#FFCC33" />
        {pending.length === 0
          ? <Empty>Nothing waiting. Flerpie proposes lines when people say something worth keeping.</Empty>
          : renderList(pending)}
      </section>

      <section aria-label="In use" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Heading label="In use" count={approved.length} />
        {approved.length === 0 ? <Empty>Nothing approved yet.</Empty> : renderList(approved)}
      </section>

      <section aria-label="Retired" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          type="button"
          onClick={() => setShowRetired((v) => !v)}
          aria-expanded={showRetired}
          aria-controls="rumor-mill-retired"
          className="np-state outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
          style={{
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: 'none', padding: '4px 6px', margin: '-4px -6px',
            borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', color: '#888',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700 }}>
            Retired <span style={{ fontWeight: 400, color: '#666', fontVariantNumeric: 'tabular-nums' }}>({retired.length})</span>
          </span>
          <svg
            width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
            className={`transition-transform duration-200 motion-reduce:transition-none ${showRetired ? 'rotate-90' : ''}`}
          >
            <path d="M3.2 1.2L7.6 5L3.2 8.8L2.2 7.7L5.4 5L2.2 2.3Z" fill="currentColor" />
          </svg>
        </button>
        {showRetired && (
          <div id="rumor-mill-retired">
            {retired.length === 0 ? <Empty>Nothing retired.</Empty> : renderList(retired)}
          </div>
        )}
      </section>
    </div>
  );
};

export default FlerpieRumorMillPanel;
